import { isNonNullable } from '@nucleus/lib-shape';
import { EmbedBlockWeb, EmbedItemWeb, HtmlEmbedItemWeb, VideoEmbedItemWeb } from '@nucleus/types/web';
import { HTMLElement, nucleusClass } from '@nucleus/web-theme-elements';
import classNames from 'classnames';
import React from 'react';
import styled from 'styled-components';
import { EmbedVideoPlayer } from '../components/EmbedVideoPlayer';
import { Iframe } from '../components/Iframe';
import { BlockProps, ComponentizedTypeProps } from '../types/component';

type BlockEmbedGridProps = Extract<BlockProps, { block?: EmbedBlockWeb }>;

// Helper to normalize items for backward compatibility (items without type default to html)
const normalizeEmbedItem = (
  item: EmbedItemWeb | (Omit<EmbedItemWeb, 'type'> & { type?: 'video' | 'html' })
): EmbedItemWeb => {
  if (item.type === 'video') {
    return item as VideoEmbedItemWeb;
  }
  if (item.type === 'html') {
    return item as HtmlEmbedItemWeb;
  }
  // Backward compatibility: items without type default to html
  return {
    ...item,
    type: 'html',
    htmlString: (item as any).htmlString ?? '',
  } as HtmlEmbedItemWeb;
};

export const BlockEmbedGrid = (props: BlockEmbedGridProps): JSX.Element => {
  const items = (props.block?.items ?? []).filter(isNonNullable).map(normalizeEmbedItem);

  return (
    <LayoutGrid className={classNames([props.className, nucleusClass('block-embed')])}>
      {items.map((embed) => (
        <BlockEmbed key={embed.id} {...embed} />
      ))}
    </LayoutGrid>
  );
};

type EmbeddedMediaProps = ComponentizedTypeProps<EmbedItemWeb>;

const EmbedContainer = styled.div`
  width: 100%;
`;

const BlockEmbed = (props: EmbeddedMediaProps): JSX.Element | null => {
  switch (props.type) {
    case 'video':
      return <BlockEmbedVideo {...props} />;
    case 'html':
      return <BlockEmbedHtml {...props} />;
    default:
      return null;
  }
};

const BlockEmbedVideo = (props: ComponentizedTypeProps<VideoEmbedItemWeb>): JSX.Element => {
  return (
    <EmbedContainer className={classNames([props.className, nucleusClass('embed-item')])}>
      <EmbedVideoPlayer url={props.videoUrl} />
    </EmbedContainer>
  );
};

const BlockEmbedHtml = (props: ComponentizedTypeProps<HtmlEmbedItemWeb>): JSX.Element => {
  const sizing = props.sizing ?? 'original';

  // Handle raw sizing for HTML items
  if (sizing === 'raw') {
    return <HTMLElement innerHTML={props.htmlString} />;
  }

  return (
    <EmbedContainer className={classNames([props.className, nucleusClass('embed-item')])}>
      <Iframe content={props.htmlString} sizing={sizing} />
    </EmbedContainer>
  );
};

const LayoutGrid = styled.div`
  display: grid;
  grid-gap: 2rem;
  width: 100%;
`;
